'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { OfferProvider } from '@wwe-portal/ui/features/offer';
import { useNavigationState } from '@wwe-portal/web/hooks/offer/category-navigation-state';
import { getCachedVirtualizer } from '@wwe-portal/web/hooks/offer/use-cached-virtualItems';
import { useOfferHandler } from '@wwe-portal/web/hooks/offer/use-offer-handler';

import { useStepupOpen } from '@wwe-portal/web/hooks/offer/use-stepup-open';
import { useStoreData } from '@wwe-portal/web/hooks/offer/use-store-data';
import {
  useGetCurrencies,
  useGetUserStocks,
} from '@wwe-portal/web/providers/queries/offer';
import {
  handleUpdatePipOnVirtualizeChange,
  useViewedOfferIds,
} from '@wwe-portal/web/providers/stores/use-pip-store';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { offerToProps } from '@wwe-portal/web/utils/offer/mapper';
import {
  StoreContent,
  useVirtualizeContent,
} from '@wwe-portal/widget/store-revamp';
import { SyntheticEvent, useEffect, useMemo } from 'react';
import { CategoryNavigation } from './category-navigation';
import { OFFER_SOURCE } from '@wwe-portal/web/utils/constants/offer';
import { CategoryNavigationListener } from './category-navigation-listener';
import { sendOfferButtonIsTappedEvent } from '@wwe-portal/web/libs/analytics/offer';
import {
  useAuthStore,
  useIsAuthenticated,
} from '@wwe-portal/web/providers/stores/auth-store';
import { API_ENV } from '@wwe-portal/web/utils/config';

export const OfferSection = () => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAuthenticating } = useAuthStore();

  const [, setStepupOpen] = useStepupOpen();
  const { stockMap } = useGetUserStocks();
  const { virtualData, categoryMap, offerMap } = useStoreData();
  const { onClick, onClickPrice } = useOfferHandler(OFFER_SOURCE.SHOP, {
    getOfferById: (id: string) => offerMap.get(id),
    onClose: () => {
      if (searchParams.has('id')) {
        router.replace(pathname, {
          scroll: false,
        });
      }
    },
  });
  const { data } = useGetCurrencies();
  const currencies = data?.data.data;
  const offerViewedData = useViewedOfferIds();
  const isAuthenticated = useIsAuthenticated();

  const openStepup = () => {
    setStepupOpen(true);
  };

  const {
    rows,
    categoryIndexes: categoryIndexesByTitle,
    estimatedSizes,
  } = virtualData;

  const { virtualizer, listRef } = useVirtualizeContent({
    rows,
    estimatedSizes,
    categoryIndexes: categoryIndexesByTitle,
    options: {
      onChange: (instance) => {
        if (!instance.isScrolling) {
          handleUpdatePipOnVirtualizeChange(instance, (index) => rows[index]);

          getCachedVirtualizer().getState().cacheData({
            initialOffset: virtualizer.scrollOffset,
            initialMeasurementsCache: virtualizer.measurementsCache,
          });
        }

        useNavigationState.getState().handleVirtualChange(virtualizer);

        // Forget scroll position by default
        getCachedVirtualizer().getState().setEnabledInitialOffset(false);
      },
      ...getCachedVirtualizer().getState().getCachedVirtualizer(),
    },
  });

  const virtualItemsRendered = virtualizer.getVirtualItems().length > 0;

  useEffect(() => {
    if (!virtualItemsRendered) {
      return;
    }
    const { setVirtualizerData, setActiveId, forceActiveId } =
      useNavigationState.getState();

    setVirtualizerData(virtualizer, categoryIndexesByTitle);
    if (forceActiveId) {
      setActiveId(forceActiveId);
    }
  }, [categoryIndexesByTitle, virtualizer, virtualItemsRendered]);

  useEffect(() => {
    const offerId = searchParams.get('id');
    if (offerId && !isAuthenticating) {
      onClick(offerId, null);
      router.replace(pathname, {
        scroll: false,
      });
    }
  }, [searchParams, isAuthenticating]);

  const availableItems = useMemo(
    () => Object.keys(categoryIndexesByTitle),
    [categoryIndexesByTitle]
  );

  const handleOfferClick = (
    offerData: OfferData,
    e: SyntheticEvent<Element, Event>
  ) => {
    onClickPrice(offerData.OffersID, e as never);
    const position = Array.from(offerMap.values()).findIndex(
      (offer) => offer.OffersID === offerData.OffersID
    );

    sendOfferButtonIsTappedEvent({
      offerData,
      overriddenParameter: {
        screen_category: categoryMap.get(offerData.OffersID) || '',
        screen_subcategory: 'offer',
      },
      position: position >= 0 ? position : 0,
    });
  };

  return (
    <div>
      <CategoryNavigationListener
        availableItems={availableItems}
        enabled={virtualItemsRendered}
      />
      <CategoryNavigation availableItems={availableItems} offerMap={offerMap} />
      <OfferProvider
        offerToProps={(offer) =>
          offerToProps(offer as OfferData, {
            stockMap,
            currencies,
            isAuthenticated,
            extendFn: (offer) => ({
              ...offer,
              hasPip: !offerViewedData.has(offer.id),
            }),
          })
        }
        onBannerClick={openStepup}
        onBannerMoreClick={openStepup}
        onOfferClick={(offerData, e) => {
          if (API_ENV === 'dev' || API_ENV === 'qa') {
            console.log(offerData.OffersID);
          }
          onClick(offerData.OffersID, e as never);
        }}
        onOfferPriceClick={handleOfferClick}
      >
        <StoreContent ref={listRef} rows={rows} virtualizer={virtualizer} />
      </OfferProvider>
    </div>
  );
};
